import { crc32 } from './crc32';

/**
 * A zip writer, in about a hundred lines and no dependency.
 *
 * Everything is stored, not deflated. The bulk of a backup is JPEG and WebP,
 * which deflate cannot make smaller — it would spend seconds per export to
 * save nothing. `data.json` would compress well and is a rounding error beside
 * the photos. A stored zip opens in every unzip tool there is, which matters:
 * this file is the user's escape hatch from the app.
 *
 * No zip64. `write` refuses anything that would need it rather than emitting a
 * file that unzips to garbage.
 */

/** Past this a zip needs the 64-bit extensions, and this writer doesn't. */
const LIMIT = 0xfffffffe;

const SIGNATURE = { local: 0x04034b50, central: 0x02014b50, end: 0x06054b50 } as const;

/** Bit 11: the name below is UTF-8, not the 1989 code page. */
const UTF8 = 0x0800;

const STORED = 0;

/**
 * Every buffer built here is its own, never a view onto a SharedArrayBuffer.
 * Saying so is what lets these go straight into a Blob.
 */
type Bytes = Uint8Array<ArrayBuffer>;

export type ZipInput = {
  name: string;
  data: Blob;
};

export class ZipTooLargeError extends Error {
  constructor() {
    super('That backup is too large for one file. Export rooms separately.');
    this.name = 'ZipTooLargeError';
  }
}

/**
 * Reads each entry once, to checksum it, and hands the same Blob straight to
 * the output — the bytes are never held in a JavaScript array, so a library of
 * photos costs no more memory than its largest single file.
 */
export async function writeZip(inputs: readonly ZipInput[]): Promise<Blob> {
  const parts: BlobPart[] = [];
  const directory: Bytes[] = [];
  let offset = 0;

  for (const input of inputs) {
    const name = encodeName(input.name);
    const size = input.data.size;
    const sum = await checksum(input.data);

    if (size > LIMIT || offset > LIMIT) throw new ZipTooLargeError();

    parts.push(localHeader(name, sum, size), input.data);
    directory.push(centralEntry(name, sum, size, offset));
    offset += 30 + name.length + size;
  }

  if (offset > LIMIT) throw new ZipTooLargeError();

  const directorySize = directory.reduce((total, entry) => total + entry.length, 0);
  parts.push(...directory, end(directory.length, directorySize, offset));

  return new Blob(parts, { type: 'application/zip' });
}

/** 64KB at a time: a 25MB photo shouldn't arrive as one 25MB array. */
async function checksum(data: Blob): Promise<number> {
  const reader = data.stream().getReader();
  let sum = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) return sum;
    sum = crc32(value, sum);
  }
}

function localHeader(name: Bytes, sum: number, size: number): Bytes {
  const header = new Uint8Array(30 + name.length);
  const view = new DataView(header.buffer);

  view.setUint32(0, SIGNATURE.local, true);
  // 2.0: the floor for a file with a directory entry, which is everything.
  view.setUint16(4, 20, true);
  view.setUint16(6, UTF8, true);
  view.setUint16(8, STORED, true);
  // Modification time and date are left at zero. A backup's dates live in
  // `manifest.json`, where they mean something; a DOS timestamp on each entry
  // would only be a second, less precise copy of the same fact.
  view.setUint32(14, sum, true);
  view.setUint32(18, size, true);
  view.setUint32(22, size, true);
  view.setUint16(26, name.length, true);

  header.set(name, 30);
  return header;
}

function centralEntry(name: Bytes, sum: number, size: number, offset: number): Bytes {
  const entry = new Uint8Array(46 + name.length);
  const view = new DataView(entry.buffer);

  view.setUint32(0, SIGNATURE.central, true);
  view.setUint16(4, 20, true);
  view.setUint16(6, 20, true);
  view.setUint16(8, UTF8, true);
  view.setUint16(10, STORED, true);
  view.setUint32(16, sum, true);
  view.setUint32(20, size, true);
  view.setUint32(24, size, true);
  view.setUint16(28, name.length, true);
  view.setUint32(42, offset, true);

  entry.set(name, 46);
  return entry;
}

function end(count: number, size: number, offset: number): Bytes {
  const record = new Uint8Array(22);
  const view = new DataView(record.buffer);

  view.setUint32(0, SIGNATURE.end, true);
  view.setUint16(8, count, true);
  view.setUint16(10, count, true);
  view.setUint32(12, size, true);
  view.setUint32(16, offset, true);

  return record;
}

function encodeName(name: string): Bytes {
  return new TextEncoder().encode(name);
}
